/**
 * GA4 Measurement ID + server-side event log (order/apis/logGa4EcommerceEvent.php)
 */

export const GA4_MEASUREMENT_ID = "G-6THCS151R0";

function getOrCreateSessionKey() {
  try {
    let k = sessionStorage.getItem("ga4_portal_session");
    if (!k) {
      k = `ga4_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem("ga4_portal_session", k);
    }
    return k;
  } catch {
    return null;
  }
}

/**
 * Kirim salinan event ke backend untuk monitoring (fire-and-forget).
 */
export function logGa4EventToServer(eventName, params = {}) {
  const base = process.env.REACT_APP_BASE_API || "";
  if (!base) return;
  const url = `${base}order/apis/logGa4EcommerceEvent.php`;

  let transactionId = null;
  if (params && typeof params.transaction_id !== "undefined") {
    transactionId = String(params.transaction_id);
  }

  const body = JSON.stringify({
    event_name: eventName,
    page_path:
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "",
    measurement_id: GA4_MEASUREMENT_ID,
    session_key: getOrCreateSessionKey(),
    transaction_id: transactionId,
    payload: params,
  });

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    /* fall through */
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function gtagSafe() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * GA4 + log server
 */
export function gtagEvent(eventName, params = {}) {
  if (gtagSafe()) {
    window.gtag("event", eventName, params);
  }
  logGa4EventToServer(eventName, params);
}

export function sendSpaPageView(pagePath) {
  const path = pagePath || (typeof window !== "undefined" ? window.location.pathname : "");
  if (gtagSafe()) {
    window.gtag("event", "page_view", { page_path: path });
  }
  logGa4EventToServer("page_view", { page_path: path });
}

export function lineItemToGa4(line) {
  if (!line || typeof line !== "object") {
    return {
      item_id: "unknown",
      item_name: "item",
      price: 0,
      quantity: 1,
    };
  }
  if (line.pid != null) {
    return {
      item_id: String(line.pid),
      item_name: String(line.name || line.productname || `Product ${line.pid}`),
      price: Number(line.price) || 0,
      quantity: 1,
    };
  }
  if (line.domain) {
    return {
      item_id: String(line.domain),
      item_name: `Domain (${line.type || "domain"})`,
      price: Number(line.price) || 0,
      quantity: 1,
    };
  }
  if (line.whois) {
    return {
      item_id: String(line.whois),
      item_name: "WHOIS / privacy",
      price: Number(line.price) || 0,
      quantity: 1,
    };
  }
  if (line.premium_domain) {
    return {
      item_id: String(line.premium_domain),
      item_name: "Premium domain",
      price: Number(line.price) || 0,
      quantity: 1,
    };
  }
  return {
    item_id: String(line.type || "line"),
    item_name: String(line.type || "cart_line"),
    price: Number(line.price) || 0,
    quantity: 1,
  };
}

export function trackBeginCheckout(total, cartItems) {
  const items = Array.isArray(cartItems) ? cartItems.map(lineItemToGa4) : [];
  gtagEvent("begin_checkout", {
    currency: "IDR",
    value: Number(total) || 0,
    items,
  });
}

export function trackGa4Purchase(transactionId, total, productsList) {
  if (transactionId == null || transactionId === "") return;
  const tid = String(transactionId);
  const key = `ga4_purchase_${tid}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* continue without dedupe if storage blocked */
  }

  const items = Array.isArray(productsList) ? productsList.map(lineItemToGa4) : [];
  gtagEvent("purchase", {
    transaction_id: tid,
    value: Number(total) || 0,
    currency: "IDR",
    items,
  });
}

export function pickProductDisplayPrice(product) {
  if (!product || typeof product !== "object") return 0;
  const cycles = [
    "monthly",
    "quarterly",
    "semiannually",
    "annually",
    "biennially",
    "triennially",
  ];
  for (const c of cycles) {
    if (product[c] != null && !Number.isNaN(Number(product[c]))) {
      const n = Number(product[c]);
      if (n > 0) return n;
    }
  }
  if (product.price != null) return Number(product.price) || 0;
  return 0;
}

export function trackViewItemFromProduct(product) {
  if (!product || product.pid == null) return;
  const price = pickProductDisplayPrice(product);
  gtagEvent("view_item", {
    currency: "IDR",
    items: [
      {
        item_id: String(product.pid),
        item_name: String(product.name || product.productname || `Product ${product.pid}`),
        price,
      },
    ],
  });
}
